import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonicModule, IonModal, ToastController } from '@ionic/angular';
import { NavNyomtatvanyService } from '../nav-nyomtatvany.service';
import { AnykLapComponent } from '../anyk-lap/anyk-lap.component';
import { AnykKotes } from '../anyk-kotes';
import { kepAdatUri, keretbeNyomtat } from '../nyomtatas-keret';
import { NyomtatasLap, nyomtatandoLapok } from '../nyomtatas-lapok';
import { KeretService } from '../../keret.service';
import { modeFavicon } from '../../mode-nevek';
import { NaploService } from '../../naplo/naplo.service';
import { BerService } from '../../ber/ber.service';

/**
 *  BEVALLÁSOK (NAV-nyomtatványok) – az aktív ügyfél NAV-nyomtatványai. NAPLO és BÉR közös; a modult az
 *  útvonal adja (route data `modul`). Új bevallásnál a nyomtatványt a letöltött sablonok
 *  közül lehet választani; a NAV sablontárából (GitHub) bármelyik nyomtatvány letölthető.
 */
@Component({
  selector: 'app-nav-bevallasok',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, AnykLapComponent],
  templateUrl: './bevallasok.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', './bevallasok.component.scss'],
})
export class NavBevallasokComponent implements OnInit, OnDestroy {
  @ViewChild('ujModal') ujModal: IonModal;
  @ViewChild('igazolasModal') igazolasModal: IonModal;
  @ViewChild('katModal') katModal: IonModal;
  @HostBinding('class.ber') get berMod() { return this.modul === 'BER'; }

  modul: 'NAPLO' | 'BER' = 'NAPLO';
  bevallasok: any[] = [];
  katalogus: any[] = [];
  katHiba: string | null = null;
  szerkesztheto = false;
  katSzuro = '';
  /** Közvetlen letöltés a Nyomtatványtárban: a NAV-kód kézzel megadva (a listában nem szereplőé is). */
  katKod = '';
  /** Az „Új bevallás" ablak nyomtatvány-keresője. */
  ujKereses = '';
  /** A bevallás-lista keresője és állapotszűrője. */
  szuro = '';
  allapotSzuro = '';
  kodSzuro = '';
  /** Kötegelt nyomtatásra kijelölt bevallások. */
  kijeloltek = new Set<string>();
  nyomtatasFut = false;
  nyomtatasAllapot: string | null = null;
  nyomtatasLapok: NyomtatasLap[] = [];
  private kotesTar = new Map<string, AnykKotes>();
  katBetolt = false;
  letoltes: string | null = null;
  uj: any = null;
  /** A NAV-kapcsolat (M2M) állapota – a beállítás a Szervíz menü önálló képernyőjén. */
  m2m: any = null;
  ev = new Date().getFullYear();
  evek: number[] = [];

  constructor(private ny: NavNyomtatvanyService, private naplo: NaploService, private ber: BerService, private route: ActivatedRoute,
              private router: Router, private toast: ToastController, private alert: AlertController, private keret: KeretService) {}

  get aktiv(): { ugyfelId: string | null; ugyfelNev: string | null; ev: number; ho?: number } {
    return this.modul === 'BER' ? this.ber.aktiv : (this.naplo.aktiv as any);
  }
  get letoltottek() { return this.katalogus.filter((k) => k.letoltve); }
  /** A NAV sablontárában lévő nyomtatványok, illetve az archiváltak (kézzel felvett vagy visszavont). */
  get aktivKatalogus() { return this.szurtKatalogus.filter((k) => !k.archivalt); }
  get archivKatalogus() { return this.szurtKatalogus.filter((k) => k.archivalt); }
  get szurtKatalogus() {
    const s = this.katSzuro.trim().toLowerCase();
    return s ? this.katalogus.filter((k) => `${k.kod} ${k.nev || ''} ${k.leiras || ''}`.toLowerCase().includes(s)) : this.katalogus;
  }
  readonly allapotCimke: Record<string, string> = { tervezet: 'tervezet', hibas: 'hibás', ellenorzott: 'ellenőrzött', bekuldve: 'beküldve', befogadva: 'befogadva', elutasitva: 'elutasítva' };

  async ngOnInit() {
    this.modul = this.route.snapshot.data['modul'] === 'BER' ? 'BER' : 'NAPLO';
    //  A másik alrendszer útvonalán nyílt meg: az alrendszert is átváltjuk (menü, jogkör, visszaút).
    if (this.keret.modulba(this.modul, this.router.url)) return;
    if (this.modul === 'BER') await this.ber.init(); else await this.naplo.init();
    const most = new Date().getFullYear();
    this.evek = [most + 1, most, most - 1, most - 2, most - 3];
    this.ev = this.aktiv.ev || most;
    await Promise.all([this.betolt(), this.katalogusBetolt(), this.m2mBetolt()]);
    // Gyorsgomb (pl. BÉR → Bér adatszolgáltatás (08)): ?kod=2608 → a hónap bevallása.
    const qp = this.route.snapshot.queryParamMap;
    //  „Beküldött bevallások" nézet (Hatóság menü): csak a beküldöttek, a tárhelyről érkezett
    //  feladási/letöltési igazolással együtt – ebből látszik, hogy a beadás rendben megérkezett-e.
    this.csakBekuldott = qp.get('nezet') === 'bekuldott';
    const kod = qp.get('kod');
    /*  Az adószámla „hiányzó bevallások" listájából ÉVVEL ÉS IDŐSZAKKAL érkezünk – különben a
     *  most futó hónapra készülne a bevallás, nem arra, amit a NAV hiányol. */
    if (kod && this.aktiv.ugyfelId) {
      await this.gyorsNyit(kod, {
        ev: Number(qp.get('ev')) || undefined,
        ho: Number(qp.get('ho')) || undefined,
        negyedev: Number(qp.get('negyedev')) || undefined,
        gyakorisag: qp.get('gyakorisag') || undefined,
      });
    }
  }

  /**
   *  A hónap adott nyomtatványa: ha már van, megnyitja; ha nincs, létrehozza (BÉR 2608-nál a
   *  bérszámfejtésből kitöltve), és ha a sablon még nincs letöltve, előbb letölti a NAV-tól.
   */
  gyorsFut = false;
  async gyorsNyit(kod: string, idoszak: { ev?: number; ho?: number; negyedev?: number; gyakorisag?: string } = {}) {
    const ev = idoszak.ev || this.aktiv.ev;
    const ho = idoszak.ho || this.aktiv.ho || new Date().getMonth() + 1;
    const gyakorisag = idoszak.gyakorisag || NavBevallasokComponent.alapGyakorisag(kod);
    const negyedev = idoszak.negyedev || Math.ceil(ho / 3);
    this.gyorsFut = true;
    try {
      //  A meglévőt a MÁSIK modulban is megkeressük (a lista a párcég sorait is hozza),
      //  különben egy bérből töltődő bevallásból kettő keletkezne.
      const lista: any[] = ((await this.ny.get(`/bevallasok/${this.modul}/${this.aktiv.ugyfelId}?ev=${ev}`)) as any).sorok;
      const van = lista.find((b) => b.kod === kod && !['elutasitva'].includes(b.allapot)
        && (gyakorisag === 'negyedeves' ? Number(b.negyedev) === negyedev && !b.ho
          : gyakorisag === 'eves' ? (!b.ho && !b.negyedev) : Number(b.ho) === Number(ho)));
      if (van) { this.megnyit(van); return; }
      if (!this.katalogus.some((k) => k.kod === kod && k.letoltve)) await this.ny.post(`/sablon/${kod}/letolt`, {});
      const r: any = await this.ny.post('/bevallas', { modul: this.modul, ugyfel_id: this.aktiv.ugyfelId, kod, ev, gyakorisag,
        ho: gyakorisag === 'havi' ? ho : null, negyedev: gyakorisag === 'negyedeves' ? negyedev : null });
      this.kitoltesJelez(kod, r.kitoltes);
      await this.megnyitValasz(r);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült megnyitni a nyomtatványt.'); }
    finally { this.gyorsFut = false; }
  }

  /**
   *  A létrehozás válasza alapján nyitja meg a bevallást. A kiszolgáló ÁTTERELHETI a másik
   *  modulba: a 08-as, a KIVA-előleg és az 58-as a BÉRSZÁMFEJTÉSBŐL töltődik, ezért a könyvelési
   *  ügyfélhez létrehozva minden összeg nulla maradna. Ilyenkor a kapcsolt bér-ügyfélhez kerül –
   *  ezt kiírjuk, és a bér modul aktív ügyfelét is átállítjuk, hogy a fejléc stimmeljen.
   */
  private async megnyitValasz(r: any) {
    const celModul = String(r?.modul || this.modul).toUpperCase();
    if (r?.atterelve && celModul !== this.modul) {
      //  A negyedéves 2601 (cégautóadó) a Gépjármű modulé (2026-10-18): a cég Gépjármű-oldalára kerül.
      if (celModul === 'GEPJARMU') {
        this.figyelmeztet(`A(z) ${r.atterelve.nev} Gépjármű-oldalához került, mert a cégautóadót a Gépjármű modul számfejti (Gépjármű › Cégautóadó).`);
      } else {
        await this.ber.setAktiv({ ugyfelId: r.atterelve.ugyfel_id, ugyfelNev: r.atterelve.nev });
        //  A bevallás a BÉR-ügyfélé, de ITT nyílik meg (a kitöltő a bevallás saját moduljából dolgozik):
        //  ha a /ber/… útvonalra ugranánk, a könyvelésből nem lehetne visszatalálni.
        this.figyelmeztet(`A(z) ${r.atterelve.nev} BÉR-ügyfeléhez került, mert ez a nyomtatvány a bérszámfejtésből töltődik elő.`);
      }
    }
    this.megnyit({ id: r.id });
  }
  /* ══════════ TÁRHELY-IGAZOLÁSOK (feladási / letöltési) ══════════
   *  A beküldés után a NAV a tárhelyre küldi az igazolásokat; a kiszolgáló ezeket magától a
   *  bevalláshoz köti (nav-igazolas.js), itt pedig látszik és kézzel javítható a párosítás. */
  igazolasBevallas: any = null;
  igazolasok: any[] = [];
  parositatlanok: any[] = [];
  readonly igazolasNevek: Record<string, string> = {
    feladasi: 'feladási igazolás', letoltesi: 'letöltési igazolás', befogadas: 'NAV nyugta', hiba: 'hibajelzés', egyeb: 'igazolás',
  };
  /*  A tárgy két részre: a lényeg („Feladási igazolás”, „26KIVA, Elfogadott bizonylat nyugta (24556763)”)
   *  és a részlet (a KR-fájl neve, az időszak, a cégnév) – a hosszú fájlnév így nem feszíti szét a sort. */
  private targyTar = new Map<string, { cim: string; reszlet: string }>();
  igazolasTargy(t: string): { cim: string; reszlet: string } {
    const sz = String(t || '').trim();
    if (this.targyTar.has(sz)) return this.targyTar.get(sz)!;
    let r = { cim: sz, reszlet: '' };
    const zarojel = sz.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
    if (zarojel && /[._]/.test(zarojel[2])) r = { cim: zarojel[1], reszlet: zarojel[2] };
    else {
      const d = sz.split(/,\s*/);
      if (d.length > 2) r = { cim: d.slice(0, 2).join(', '), reszlet: d.slice(2).join(', ') };
    }
    if (this.targyTar.size > 500) this.targyTar.clear();
    this.targyTar.set(sz, r);
    return r;
  }
  igazolasNev(f: string) { return this.igazolasNevek[f] || f || 'igazolás'; }
  /** Igazolás a gépi (M2M) beküldésről – a rendszer PDF-kivonata. */
  m2mIgazolas(b: any) {
    this.ny.fajlMegnyit(`/api/nav-nyomtatvany/bevallas/${b.id}/m2m-igazolas.pdf`, `${b.kod} – igazolás a gépi beküldésről`);
  }
  /*  HELYESBÍTÉS / ÖNELLENŐRZÉS meglévő bevallásból: az adatok másolata, a nyomtatvány „Bevallás
   *  jellege” mezőjében H vagy O jelöléssel (önellenőrzés önellenőrzéséből ismételt önellenőrzés). */
  async javitoKeszit(b: any) {
    const a = await this.alert.create({
      header: 'Javító bevallás',
      message: `A(z) ${b.kod} (${this.idoszakSzoveg(b)}) adataiból új bevallás készül. Önellenőrzésnél az önellenőrzési `
        + 'pótlék és a különbözet sorait a nyomtatvány szabályai szerint töltsd ki – a kitöltő ellenőrzése jelzi, mi hiányzik.',
      inputs: [
        { type: 'radio', label: 'Helyesbítés (adózói javítás – H)', value: 'H', checked: b.jelleg !== 'O' },
        { type: 'radio', label: b.jelleg === 'O' ? 'Ismételt önellenőrzés (O)' : 'Önellenőrzés (O)', value: 'O', checked: b.jelleg === 'O' },
      ],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Létrehozás', role: 'ok' }],
    });
    await a.present();
    const v = await a.onDidDismiss();
    if (v.role !== 'ok' || !v.data?.values) return;
    try {
      const r: any = await this.ny.post(`/bevallas/${b.id}/javito`, { jelleg: v.data.values });
      this.ok(r.ismetelt ? 'Ismételt önellenőrzés létrehozva.' : (r.jelleg === 'O' ? 'Önellenőrzés létrehozva.' : 'Helyesbítés létrehozva.'));
      this.megnyit({ id: r.id });   // abban a modulban, ahol vagyunk (mint a sima megnyitás)
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült létrehozni.'); }
  }
  bekuldott(b: any) { return ['bekuldve', 'befogadva', 'elutasitva'].includes(b?.allapot); }
  /** „Beküldött bevallások" nézet: a lista a beadott bevallásokra szűkül, az igazolásokkal. */
  csakBekuldott = false;
  get igazolasHianyzik(): number {
    //  Gépi beküldésnél a NAV nyugtája a várt igazolás, egyébként a feladási + letöltési.
    return this.bevallasok.filter((b) => this.bekuldott(b)
      && (b.m2m_bekuldve ? !b.igazolas_nyugta : !(b.igazolas_feladasi && b.igazolas_letoltesi))).length;
  }
  /** A tárhely-üzenetek újrapárosítása a bevallásokkal (a tárhely-szinkron után magától is fut). */
  parositasFut = false;
  async parositasIndit() {
    const uid = this.naploUgyfelId;
    if (!uid) { this.hiba('Ehhez a céghez nincs könyvelési ügyfél (a tárhely üzenetei oda érkeznek).'); return; }
    this.parositasFut = true;
    try {
      const r: any = await this.ny.igazolas.post(`/parositas/${uid}`, {});
      await this.betolt();
      this.ok(`${r?.parositott || 0} igazolás párosítva (${r?.vizsgalt || 0} üzenet átnézve).`);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült a párosítás.'); }
    finally { this.parositasFut = false; }
  }

  /** A tárhely-üzenetek a KÖNYVELÉSI ügyfélhez tartoznak – a bér oldaláról a párcéget használjuk. */
  private get naploUgyfelId(): string | null {
    if (this.modul === 'NAPLO') return this.aktiv.ugyfelId;
    const p = this.parok.find((x) => x.modul === 'NAPLO');
    return p ? p.ugyfel_id : null;
  }

  async igazolasokNyit(b: any) {
    this.igazolasBevallas = b;
    this.igazolasok = b.igazolasok || [];
    this.parositatlanok = [];
    await this.igazolasModal.present();
    const uid = this.naploUgyfelId;
    if (uid) this.parositatlanok = await this.ny.igazolas.get(`/parositatlan/${uid}`).catch(() => []);
  }
  async igazolasFrissit() {
    this.igazolasok = await this.ny.igazolas.get(`/bevallas/${this.igazolasBevallas.id}`).catch(() => []);
    const uid = this.naploUgyfelId;
    this.parositatlanok = uid ? await this.ny.igazolas.get(`/parositatlan/${uid}`).catch(() => []) : [];
    await this.betolt();
  }
  async igazolasHozzakot(u: any) {
    try {
      await this.ny.igazolas.put(`/bevallas/${this.igazolasBevallas.id}`, { uzenet_id: u.id, fajta: u.fajta });
      await this.igazolasFrissit();
      this.ok('Az igazolás hozzákötve a bevalláshoz.');
    } catch { this.hiba('Nem sikerült hozzákötni.'); }
  }
  async igazolasLevalaszt(i: any) {
    try {
      await this.ny.igazolas.put(`/bevallas/${this.igazolasBevallas.id}`, { uzenet_id: i.id, levalaszt: true });
      await this.igazolasFrissit();
    } catch { this.hiba('Nem sikerült leválasztani.'); }
  }
  /** Az igazolás PDF-je a tárhely mellékleteiből (tokennel, a DataService-en keresztül). */
  igazolasMegnyit(i: any) { this.ny.fajlMegnyit(`/api/naplo/tarhely/melleklet/${i.id}`, 'Igazolás'); }

  private async figyelmeztet(m: string) { (await this.toast.create({ message: m, duration: 5000, color: 'warning', position: 'top' })).present(); }

  /*  A lista a cég MINDKÉT oldalát mutatja: ha a könyvelési és a bérszámfejtési ügyfél össze van
   *  kötve (ugyanaz az adózó), a másik modul bevallásai is itt vannak – onnan is beadhatók. */
  parok: { modul: string; ugyfel_id: string; nev: string }[] = [];
  async betolt() {
    if (!this.aktiv.ugyfelId) return;
    try {
      const r: any = await this.ny.get(`/bevallasok/${this.modul}/${this.aktiv.ugyfelId}?ev=${this.ev}`);
      this.bevallasok = r.sorok || [];
      this.parok = r.parok || [];
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni a bevallásokat.'); }
  }
  /*  SZŰRÉS: szabad szöveg (kód, megnevezés, időszak, rögzítő), nyomtatványkód és állapot.
   *  A kijelölés, a „mind" és a kötegelt nyomtatás a SZŰRT listára vonatkozik – amit lát, azt nyomtatja. */
  get szurtBevallasok(): any[] {
    const sz = this.szuro.trim().toLowerCase();
    return this.bevallasok.filter((b) => {
      if (this.csakBekuldott && !this.bekuldott(b)) return false;
      if (this.kodSzuro && b.kod !== this.kodSzuro) return false;
      if (this.allapotSzuro && b.allapot !== this.allapotSzuro) return false;
      if (!sz) return true;
      return `${b.kod} ${b.cim || ''} ${this.idoszakSzoveg(b)} ${b.ki || ''} ${this.allapotCimke[b.allapot] || ''}`.toLowerCase().includes(sz);
    });
  }
  /** A listában előforduló nyomtatványkódok és állapotok – csak amire tényleg lehet szűrni. */
  get kodok(): string[] { return [...new Set(this.bevallasok.map((b) => b.kod))].sort(); }
  get allapotok(): string[] { return [...new Set(this.bevallasok.map((b) => b.allapot))]; }
  szuroTorol() { this.szuro = ''; this.kodSzuro = ''; this.allapotSzuro = ''; }
  /** A másik modulból idelátszó bevallások (a párügyfélé). */
  get masModulbol() { return this.bevallasok.filter((b) => b.sajat === false).length; }
  modulCim(m: string) { return m === 'BER' ? 'Bér' : m === 'GEPJARMU' ? 'Gépjármű' : 'Könyvelés'; }
  async katalogusBetolt() {
    this.katBetolt = true;
    try {
      const r: any = await this.ny.get('/katalogus'); this.katalogus = r.lista || []; this.katHiba = r.hiba; this.szerkesztheto = !!r.szerkesztheto;
      if (r.feladat) { this.tomeges = r.feladat; if (r.feladat.fut) this.tomegesFigyel(); }
    }
    catch { this.katHiba = 'A katalógus nem érhető el.'; }
    finally { this.katBetolt = false; }
  }

  /* Az időszak: havi, negyedéves (pl. 26KIVA-előleg) vagy éves – a nyomtatvány szokásos
   * gyakoriságát ajánljuk (a kiszolgáló alapGyakorisag-ának párja), de átállítható. */
  readonly gyakorisagok = [{ k: 'havi', cim: 'Havi' }, { k: 'negyedeves', cim: 'Negyedéves' }, { k: 'eves', cim: 'Éves' }];
  /*  A kiválasztott nyomtatványnak értelmes időszakok (a kiszolgáló a sablonból állapítja meg):
   *  eseti nyomtatványnál nincs év/hónap, a KIVA negyedéves vagy éves, a 08-as csak havi. */
  ujIdoszakok(): string[] {
    const k = this.katalogus.find((x) => x.kod === this.uj?.kod);
    return k?.idoszakok?.length ? k.idoszakok : ['havi', 'negyedeves', 'eves'];
  }
  get ujGyakorisagok() { const l = this.ujIdoszakok(); return this.gyakorisagok.filter((g) => l.includes(g.k)); }
  /** A bejelentőknél (T1041, T1042E) a hónap az előtöltés forrása: az abban történt változások. */
  get ujHonapCim(): string { return /^T104/.test(this.uj?.kod || '') ? 'A bejelentendő változások hónapja' : 'Hónap'; }
  get ujEseti(): boolean { return this.ujIdoszakok()[0] === 'eseti'; }
  private ujGyakorisagIgazit() {
    if (!this.uj) return;
    const l = this.ujIdoszakok();
    if (l.includes(this.uj.gyakorisag)) return;
    const alap = NavBevallasokComponent.alapGyakorisag(this.uj.kod);
    this.uj.gyakorisag = l.includes(alap) ? alap : l[0];
  }
  readonly romai = ['I', 'II', 'III', 'IV'];
  readonly honapNevek = ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];
  negyedevHonapok(n: number) { return `${this.honapNevek[n * 3 - 3].slice(0, 3)}.–${this.honapNevek[n * 3 - 1].slice(0, 3)}.`; }
  static alapGyakorisag(kod: string): string {
    const k = String(kod || '').toUpperCase();
    if (/KIVA$/.test(k)) return 'negyedeves';
    if (/^\d{2}08$/.test(k)) return 'havi';
    if (/^\d{2}58$/.test(k)) return 'negyedeves';
    if (/^\d{2}65$/.test(k) || /^T104/.test(k)) return 'havi';
    //  A többi időszakos nyomtatvány túlnyomó része éves bevallás (TAO, KATA, iparűzési adó …).
    return 'eves';
  }
  /** Mit tud a modul előtölteni (a kiszolgáló kitoltheto()-jének párja – csak tájékoztatás). */
  get ujKitoltheto(): string | null {
    if (!this.uj) return null;
    if (this.modul === 'BER') {
      if (this.uj.kod === '2608' && this.uj.gyakorisag === 'havi') return 'a hónap bérszámfejtéséből';
      if (/KIVA$/i.test(this.uj.kod || '') && this.uj.gyakorisag === 'negyedeves') return 'a negyedév bérszámfejtéséből és juttatásaiból (KIVA-előleg)';
    }
    //  A negyedéves 2601 a Gépjármű cégautóadó-számfejtéséből (a kiszolgáló oda is tereli, ha ott van jog).
    if (/^\d{2}01$/.test(this.uj.kod || '') && this.uj.gyakorisag === 'negyedeves') return 'a Gépjármű modul negyedéves cégautóadó-számfejtéséből (02-es lap)';
    // Modul-forrás nélkül is jár az adózó azonosítása az ügyféltörzsből (kiszolgáló: TORZS_FORRAS).
    return 'az ügyfél törzsadatából (cégnév, adószám, székhely, elérhetőség)';
  }
  idoszakSzoveg(b: any): string {
    if (b.gyakorisag === 'eseti') return `eseti (${b.ev})`;
    if (b.gyakorisag === 'negyedeves' || (!b.ho && b.negyedev)) return `${b.ev}. ${this.romai[(b.negyedev || 1) - 1]}. negyedév`;
    if (b.ho) return `${b.ev}. ${String(b.ho).padStart(2, '0')}. hó`;
    return `${b.ev}. év`;
  }

  /** A választható (letöltött) nyomtatványok a keresés szerint szűrve. */
  get ujValaszthatok() {
    const s = this.ujKereses.trim().toLowerCase();
    const l = this.letoltottek;
    return s ? l.filter((k) => `${k.kod} ${k.nev || ''} ${k.leiras || ''}`.toLowerCase().includes(s)) : l;
  }
  /** Keresés közben: ha a kiválasztott kód kiesett a szűrésből, az első találatra állunk. */
  ujKeresesValt() {
    const l = this.ujValaszthatok;
    if (l.length && !l.some((k) => k.kod === this.uj?.kod)) { this.uj.kod = l[0].kod; this.ujKodValt(); }
  }
  /*  INDÍTÁS A NYOMTATVÁNYTÁRBÓL: a tár bezárul, és az Új bevallás ablak ezzel a nyomtatvánnyal
   *  (és a hozzá illő időszakkal) nyílik meg. */
  async tarbolIndit(k: any) {
    if (!this.aktiv.ugyfelId) { this.figyelmeztet('Előbb válassz ügyfelet – a bevallás az ő nevére készül.'); return; }
    await this.katModal.dismiss();
    this.ujNyit(k.kod);
  }
  ujNyit(kod?: string) {
    this.ujKereses = '';
    const alap = (kod && this.letoltottek.some((k) => k.kod === kod) ? kod : null)
      || this.letoltottek.find((k) => (this.modul === 'BER' ? k.kod === '2608' : true))?.kod || this.letoltottek[0]?.kod || null;
    const ho = this.aktiv.ho || new Date().getMonth() + 1;
    this.uj = { kod: alap, ev: this.ev, ho, negyedev: Math.ceil(ho / 3), gyakorisag: NavBevallasokComponent.alapGyakorisag(alap) };
    this.ujGyakorisagIgazit();
    this.ujModal.present();
  }
  ujKodValt() { if (this.uj) { this.uj.gyakorisag = NavBevallasokComponent.alapGyakorisag(this.uj.kod); this.ujGyakorisagIgazit(); } }
  /*  A létrehozás eltarthat pár másodpercig (a sablon első megnyitásakor a modell újraépül, és
   *  az előtöltés is lefut), ezért a gomb a művelet végéig zárva marad – különben a türelmetlen
   *  második kattintás újabb bevallást hozott létre. A kiszolgáló is véd ellene. */
  ujFut = false;
  async ujLetrehoz() {
    const u = this.uj;
    if (!u || this.ujFut) return;
    this.ujFut = true;
    try {
      const r: any = await this.ny.post('/bevallas', { modul: this.modul, ugyfel_id: this.aktiv.ugyfelId, kod: u.kod, ev: u.ev, gyakorisag: u.gyakorisag,
        ho: u.gyakorisag === 'havi' ? u.ho : null, negyedev: u.gyakorisag === 'negyedeves' ? u.negyedev : null });
      await this.ujModal.dismiss();
      this.kitoltesJelez(u.kod, r.kitoltes);
      await this.megnyitValasz(r);
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült létrehozni.'); }
    finally { this.ujFut = false; }
  }
  /** Az előtöltés visszajelzése (mennyi mező, figyelmeztetések). */
  kitoltesJelez(kod: string, k: any) {
    if (!k) return;
    this.ok(`${kod} előtöltve: ${k.mlapok != null ? k.mlapok + ' M-lap, ' : ''}${k.mezok} mező${k.kihagyva?.length ? `, ${k.kihagyva.length} kihagyva` : ''}.`);
    for (const f of k.figyelmeztetesek || []) this.figyelmeztet(f);
  }
  /*  A NAV-KAPCSOLAT (M2M) állapota. Maga a beállítás önálló képernyő a Szervíz menüben
   *  (/…/nav-kapcsolat) – itt csak azt mutatjuk, hogy be van-e állítva, és melyik környezet megy. */
  get m2mKesz(): boolean { return !!this.m2m?.[this.m2m?.hasznalt]?.kesz; }
  get m2mCim(): string {
    if (!this.m2m) return 'NAV-kapcsolat beállítása';
    const k = this.m2m.hasznalt === 'eles' ? 'éles' : 'teszt';
    return this.m2mKesz
      ? `A beküldés a NAV ${k} környezetébe megy. Kattints a beállításhoz.`
      : 'A gépi beküldéshez nincs (teljes) hitelesítő. Kattints a beállításhoz.';
  }
  private async m2mBetolt() {
    if (!this.aktiv.ugyfelId) { this.m2m = null; return; }
    try { this.m2m = await this.ny.get(`/m2m/${this.modul}/${this.aktiv.ugyfelId}`); } catch { this.m2m = null; }
  }
  navKapcsolatra() { this.router.navigate([this.modul === 'BER' ? '/ber/nav-kapcsolat' : '/naplo/nav-kapcsolat']); }

  /*  A bevallás ABBAN a modulban nyílik, ahol épp vagyunk – akkor is, ha a párcéghez (másik
   *  modulhoz) tartozik. Korábban a saját moduljára ugrott, így a könyvelésből egy bérbevallás
   *  megnyitása átdobott a BÉR-be; a kitöltő a bevallás saját moduljából dolgozik, nem a képernyőéből. */
  megnyit(b: any) {
    //  A külső beküldésnek (ÁNYK / ONYA) nincs nálunk kitöltött adata: az igazolásai nyílnak meg.
    if (b?.forras === 'kulso') { this.igazolasokNyit(b); return; }
    this.router.navigate([this.modul === 'BER' ? '/ber/bevallas' : '/naplo/bevallas', b.id]);
  }

  /*  KÖZVETLEN LETÖLTÉS KÓD ALAPJÁN.
   *  A lista a NAV GitHub-tárolóiból épül; ami ott nincs fent (pl. csak az online kitöltőben élő
   *  nyomtatvány), az így is lehozható – a kiszolgáló ugyanazt az utat járja be (XSD → ONYA). */
  /*  A NAV-nál érvényes bizonylattípus/verzió (gépi beküldés).
   *  A NAV a beküldésnél nem a nyomtatvány kódját és az XSD verzióját várja, hanem egy nála
   *  nyilvántartott párost (pl. T1042E_1.0). Üresen hagyva a kód és az XSD verziója megy; a
   *  rendszer maga is beírja ide, ha a NAV-ellenőrzés közben talál működő verziót. */
  async m2mAzonosito(k: any) {
    const a = await this.alert.create({
      header: `${k.kod} – NAV bizonylat-azonosító`,
      message: 'A gépi beküldésnél ezt a típust és verziót küldjük a NAV-nak. Üresen hagyva a nyomtatvány kódja és az XSD verziója megy.',
      inputs: [{ name: 'tipus', placeholder: `Bizonylattípus (alap: ${k.kod})`, value: k.m2m_tipus || '' },
        { name: 'verzio', placeholder: `Bizonylatverzió (alap: ${k.verzio || '1.0'})`, value: k.m2m_verzio || '' }],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Mentés', role: 'ok' }],
    });
    await a.present();
    const v = await a.onDidDismiss();
    if (v.role !== 'ok') return;
    try {
      await this.ny.put(`/sablon/${k.kod}/m2m`, { m2m_tipus: v.data?.values?.tipus || '', m2m_verzio: v.data?.values?.verzio || '' });
      await this.katalogusBetolt();
      this.ok('Bizonylat-azonosító mentve.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }

  async katKodLetolt() {
    const kod = this.katKod.trim().toUpperCase().replace(/\s+/g, '');
    if (!kod) return;
    await this.sablonLetolt({ kod });
    if (this.katalogus.some((k) => k.kod === kod && k.letoltve)) { this.katKod = ''; this.katSzuro = kod; }
  }
  /* ── KÖTEGELT NYOMTATÁS ──
   *  A listából, a bevallások megnyitása nélkül: minden kijelölt bevallás kitöltött lapjai egy
   *  sorozatban, A4-re kicsinyítve. A lapkép ↔ adat leképezés ugyanaz (anyk-kotes.ts), mint a
   *  kitöltőben, tehát a papír ugyanaz, mintha egyenként nyomtatnánk. */
  get logo() { return modeFavicon(this.modul); }
  kijelol(b: any, be: boolean) { if (be) this.kijeloltek.add(b.id); else this.kijeloltek.delete(b.id); }
  get mindKijelolve() { const l = this.szurtBevallasok; return !!l.length && l.every((b) => this.kijeloltek.has(b.id)); }
  kijelolMind(be: boolean) { this.kijeloltek = new Set(be ? this.szurtBevallasok.filter((b) => b.forras !== 'kulso').map((b) => b.id) : []); }

  async kotegNyomtat() {
    const sorok = this.szurtBevallasok.filter((b) => this.kijeloltek.has(b.id));
    await this.nyomtatas(sorok.length ? sorok : []);
  }
  async nyomtatas(sorok: any[]) {
    if (!sorok.length || this.nyomtatasFut) return;
    this.nyomtatasAllapot = 'Lapok összeállítása…';
    let lapok: NyomtatasLap[] = [];
    try {
      lapok = await nyomtatandoLapok(this.ny, sorok, this.kotesTar,
        (b) => this.figyelmeztet(`${b.kod}: ehhez nincs ÁNYK-lapkép, nyomtatni csak megnyitva lehet.`));
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült összeállítani a nyomtatást.'); this.nyomtatasAllapot = null; return; }
    if (!lapok.length) { this.figyelmeztet('Nincs nyomtatható lap a kijelölt bevallásokban.'); this.nyomtatasAllapot = null; return; }
    const logoUri = await kepAdatUri(this.logo);
    this.nyomtatasLapok = lapok;
    this.nyomtatasFut = true;
    this.nyomtatasAllapot = `${lapok.length} lap nyomtatása…`;
    setTimeout(async () => {
      const forras = document.querySelector('.any-nyomtat') as HTMLElement | null;
      if (!forras) { this.nyomtatasFut = false; this.nyomtatasAllapot = null; return; }
      const cim = sorok.length === 1 ? `${sorok[0].kod} – ${sorok[0].cim}` : `${sorok.length} bevallás – ${this.aktiv.ugyfelNev || ''}`;
      try { await keretbeNyomtat(forras, cim, { url: this.logo, adatUri: logoUri }); }
      finally { this.nyomtatasFut = false; this.nyomtatasAllapot = null; }
    }, 400);
  }

  /*  NAV-TÁR ELLENŐRZÉSE: friss lista a GitHubról – mi jelent meg, mi tűnt el. */
  async tarEllenoriz() {
    this.letoltes = '#tar';
    try {
      const r: any = await this.ny.post('/katalogus/ellenoriz', {});
      const valtozas = (r.ujVerzio?.length ? ` · új sémaverzió: ${r.ujVerzio.map((x: any) => x.kod + (x.uj && x.uj !== x.regi ? ` (v${x.regi || '?'} → v${x.uj})` : '')).join(', ')}` : '')
        + (r.xpathValtozott?.length ? ` · megváltozott XPath: ${r.xpathValtozott.map((x: any) => x.kod).join(', ')}` : '');
      const msg = `A NAV sablontárában ${r.osszes} nyomtatvány van`
        + (r.uj?.length ? ` · új: ${r.uj.join(', ')}` : ' · nincs új nyomtatvány')
        + (r.eltunt?.length ? ` · archiválva (eltűnt a tárból): ${r.eltunt.join(', ')}` : '')
        + (valtozas || ' · a letöltött sablonok és XPath-állományok változatlanok');
      if (valtozas) this.figyelmeztet(msg); else this.ok(msg);
      await this.katalogusBetolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült ellenőrizni a NAV-tárat.'); }
    finally { this.letoltes = null; }
  }
  /*  ÖSSZES LETÖLTÉSE / FRISSÍTÉSE a kiszolgálón, háttérben; az állapotot pár másodpercenként kérdezzük. */
  tomeges: any = null;
  private tomegesIdozito: any = null;
  async tomegesIndit(mod: 'letolt' | 'frissit') {
    const db = this.aktivKatalogus.filter((k) => !k.technikai && (mod === 'frissit' ? k.letoltve : !k.letoltve)).length;
    if (!db) { this.figyelmeztet(mod === 'frissit' ? 'Nincs letöltött nyomtatvány a NAV-tárból.' : 'Minden nyomtatvány le van már töltve.'); return; }
    try {
      const r: any = await this.ny.post('/tar/osszes', { mod });
      this.tomeges = r.feladat;
      this.ok(`${mod === 'frissit' ? 'Frissítés' : 'Letöltés'} elindult a háttérben (${r.feladat?.ossz} nyomtatvány) – az ablak bezárható.`);
      this.tomegesFigyel();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült elindítani.'); }
  }
  private tomegesFigyel() {
    if (this.tomegesIdozito) return;
    let utolsoKesz = this.tomeges?.kesz ?? 0;
    this.tomegesIdozito = setInterval(async () => {
      try {
        const r: any = await this.ny.get('/tar/osszes');
        this.tomeges = r.feladat;
        if (!r.feladat?.fut) {
          clearInterval(this.tomegesIdozito); this.tomegesIdozito = null;
          await this.katalogusBetolt();
          if (r.feladat) this.ok(`Kész: ${r.feladat.sikeres.length} nyomtatvány${r.feladat.hibak.length ? `, ${r.feladat.hibak.length} hibával` : ''}.`);
        } else if (r.feladat.kesz - utolsoKesz >= 10) { utolsoKesz = r.feladat.kesz; await this.katalogusBetolt(); }
      } catch { /* a következő körben újra */ }
    }, 3000);
  }
  ngOnDestroy() { if (this.tomegesIdozito) clearInterval(this.tomegesIdozito); }
  /*  KÉZZEL FELVETT SABLON TÖRLÉSE (csak archivált, és amihez nincs bevallás – a kiszolgáló ellenőrzi). */
  async sablonTorol(k: any) {
    const a = await this.alert.create({ header: `${k.kod} törlése`,
      message: 'A kézzel felvett nyomtatványsablon törlődik a nyomtatványtárból. (Az offline ÁNYK-csomag megmarad.)',
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'ok' }] });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'ok') return;
    this.letoltes = k.kod;
    try { await this.ny.del(`/sablon/${k.kod}`); this.ok(`${k.kod} törölve.`); await this.katalogusBetolt(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
    finally { this.letoltes = null; }
  }
  async sablonLetolt(k: any) {
    this.letoltes = k.kod;
    try {
      // A kiszolgáló az XSD mellé az ÁNYK-sablont is lehozza a NAV-tól (lapkép, ellenőrzések, kódok).
      const r: any = await this.ny.post(`/sablon/${k.kod}/letolt`, {});
      this.ok(`${r.kod} letöltve: ${r.mezoDb} mező (${r.formok.join(', ')})`
        + (r.anyk ? ` · ÁNYK-sablon v${r.anyk.anykVerzio} (${r.anyk.kodosMezok} kódszótár)` : ''));
      if (r.anykHiba) this.figyelmeztet(`${r.kod}: ${r.anykHiba} – a nyomtatvány az XSD-ből épített nézettel tölthető ki.`);
      await this.katalogusBetolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült letölteni.'); }
    finally { this.letoltes = null; }
  }

  /*  ÁNYK-KINÉZET MINDEN LETÖLTÖTT NYOMTATVÁNYHOZ. Ahol hiányzik vagy elavult, lehozza a NAV
   *  csomagját (a más néven futókat is, pl. a T1041-et a „08E" csomagból), és összeveti az XSD-vel:
   *  ha az XSD-ben van olyan mező, ami az ÁNYK-lapon nincs, azt kiírja – az XSD a mérvadó. */
  async anykMind() {
    this.letoltes = '*';
    try {
      const r: any = await this.ny.post('/sablon/anyk-frissites', {});
      const sorok = r.sorok || [];
      const uj = sorok.filter((s: any) => s.allapot === 'betöltve' || s.allapot === 'frissítve').length;
      const nincs = sorok.filter((s: any) => s.allapot === 'nincs ÁNYK-csomag');
      this.ok(`${sorok.length} nyomtatvány átnézve · ${uj} kapott friss ÁNYK-kinézetet`);
      for (const s of nincs) this.figyelmeztet(`${s.kod}: nincs ÁNYK-csomag a NAV-nál – az XSD-ből épített nézet marad.`);
      await this.katalogusBetolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült frissíteni.'); }
    finally { this.letoltes = null; }
  }

  /** ÁNYK-sablon (újra)töltése a NAV katalógusából – ettől lesz igazi lapkép és kódszótár. */
  async xpathEllenoriz(k: any) {
    this.letoltes = k.kod;
    try {
      const r: any = await this.ny.post(`/sablon/${k.kod}/xpath`, {});
      if (r.hiba) this.figyelmeztet(`${k.kod}: a NAV sablontára most nem érhető el (${r.hiba}).`);
      else if (r.van) this.ok(`${k.kod}: a NAV gépi úton fogadja (${r.fajl}).`);
      else this.figyelmeztet(`${k.kod}: a NAV sablontárában még nincs XPath-állomány – gépi úton még nem küldhető be.`);
      await this.katalogusBetolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült ellenőrizni.'); }
    finally { this.letoltes = null; }
  }
  async enykNavbol(k: any) {
    this.letoltes = k.kod;
    try {
      const r: any = await this.ny.post(`/sablon/${k.kod}/enyk/nav`, {});
      const f = (r.formok || []).map((x: any) => `${x.id}: ${x.lapok} lap, ${x.szabalyok} szabály`).join('; ');
      this.ok(`${k.kod} ÁNYK-sablon v${r.anykVerzio} (${r.anykDatum}${r.anykNev && r.anykNev !== k.kod ? `, „${r.anykNev}” csomag` : ''}) – ${f}; ${r.kodosMezok} kódszótár.`);
      // Az XSD a mérvadó: ha van olyan mezője, ami az ÁNYK-lapon nincs, azt jelezzük.
      for (const e of r.egyezes?.formok || []) {
        if (e.csak_xsd) this.figyelmeztet(`${e.form}: ${e.csak_xsd} XSD-mező nincs az ÁNYK-lapon (pl. ${(e.pelda || []).join(', ')}) – ezek csak a táblázatos nézetben tölthetők.`);
      }
      await this.katalogusBetolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült letölteni a NAV-tól.'); }
    finally { this.letoltes = null; }
  }

  /** ÁNYK-sablon (.enyk) betöltése: feltöltött fájlból, vagy a kiszolgálóra csomagoltból (fájl nélkül). */
  async enykBetolt(k: any, fajl?: File) {
    this.letoltes = k.kod;
    try {
      const fd = new FormData();
      if (fajl) fd.append('file', fajl);
      const r: any = await this.ny.post(`/sablon/${k.kod}/enyk`, fd);
      const f = (r.formok || []).map((x: any) => `${x.id}: ${x.lapok} lap, ${x.szabalyok} szabály`).join('; ');
      this.ok(`ÁNYK-sablon v${r.verzio} betöltve – ${f}.`);
      await this.katalogusBetolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni az ÁNYK-sablont.'); }
    finally { this.letoltes = null; }
  }
  enykFajl(k: any, ev: any) { const f = ev.target.files?.[0]; if (f) this.enykBetolt(k, f); ev.target.value = ''; }

  async torol(b: any) {
    const a = await this.alert.create({ header: 'Törlés', message: `Biztosan törli: ${b.cim}?`, buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive' }] });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'destructive') return;
    try { await this.ny.del('/bevallas/' + b.id); await this.betolt(); } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2200, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3200, color: 'danger' })).present(); }
}
